/**
 * The drill-in STACK's rules, with no React and no imports.
 *
 * Symphony's entity drill-ins (a goal, a task, an agent's skills, a file) all open into one host. That
 * host used to hold exactly ONE of them, so opening a task from inside a goal replaced the goal — and
 * the task's only way out dropped the reader all the way to the grid, with the goal they were reading
 * gone. Reported in those words: *"I have no way to go back to the goal since the task dialog opens and
 * replaces the goals dialog."*
 *
 * Four rules, each of them the answer to a way this can be got wrong:
 *
 * **1. The route IN decides the route OUT.** `resolveBack` answers from the stack's DEPTH, never from
 * whether a label happens to be set. A task opened from the Tasks list sits at depth 1 and gets no back
 * affordance, so it can never grow one pointing at a goal it did not come from — and no call site has
 * to remember to suppress it.
 *
 * **2. Closing means closing.** `clearModals` empties the whole stack. A reader who wants out of a
 * two-deep stack presses the close control once; folding close into back is the same complaint one
 * level down.
 *
 * **3. Back must NAME its destination.** A bare arrow cannot say whether it returns to the goal or
 * leaves entirely. `resolveBack` returns nothing without a label, so an unlabelled push degrades to no
 * back rather than to an ambiguous one.
 *
 * **4. Only the TOP entry renders.** Two live modals means two scrims and two Escape handlers. That is
 * `topModal`'s whole job, and it is safe to unmount the entries beneath because a drill-in reads its
 * entity from the React Query cache — returning re-selects the live copy rather than restoring a stale
 * snapshot.
 *
 * Import-free on purpose, exactly like `lib/goal-rounds.ts` and `lib/goal-ledger.ts`: orion-web's jest
 * config cannot parse the ESM common-lib pulls in, so a unit test may not touch the app's dependency
 * graph.
 */

/** One open drill-in. `node` is deliberately `unknown` here — the rules never look inside it. */
export interface ModalEntry<TNode = unknown> {
  node: TNode;
  /** The owning entity's id, surfaced so its card can pin the DS Active state. */
  id: string | null;
  /** What the entry BENEATH this one is called. Absent on a drill-in opened from a page. */
  backLabel?: string;
}

/** Push a drill-in onto the stack. */
export function pushModal<T>(stack: ModalEntry<T>[], entry: ModalEntry<T>): ModalEntry<T>[] {
  return [...stack, entry];
}

/** Return to the drill-in underneath. A no-op on an empty stack rather than an error. */
export function popModal<T>(stack: ModalEntry<T>[]): ModalEntry<T>[] {
  return stack.slice(0, -1);
}

/** Dismiss everything. See rule 2. */
export function clearModals<T>(): ModalEntry<T>[] {
  return [];
}

/** The only entry that renders. See rule 4. */
export function topModal<T>(stack: ModalEntry<T>[]): ModalEntry<T> | undefined {
  return stack.length ? stack[stack.length - 1] : undefined;
}

/**
 * The back affordance this stack offers, or nothing.
 *
 * Both halves are required: something underneath to return TO (rule 1), and a label to name it
 * (rule 3). Missing either, the caller renders no back control at all.
 */
export function resolveBack<T>(stack: ModalEntry<T>[]): { label: string } | undefined {
  if (stack.length <= 1) return undefined;
  const label = topModal(stack)?.backLabel?.trim();
  return label ? { label } : undefined;
}
